/**
 * Authentication API service
 * Handles all HTTP calls to backend auth endpoints
 */

import api from '@/lib/api';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RefreshTokenResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  ResendCodeRequest,
  ResendCodeResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  AuthError,
} from '@/types/auth';

/**
 * Register a new user
 * @throws {AuthError} on validation or server errors
 */
export async function register(request: RegisterRequest): Promise<RegisterResponse> {
  try {
    const response = await api.post<RegisterResponse>('/auth/register', request);
    return response.data;
  } catch (error) {
    // Error already transformed by axios interceptor
    throw error as AuthError;
  }
}

/**
 * Login with username and password
 * @throws {AuthError} on authentication failure
 */
export async function login(request: LoginRequest): Promise<LoginResponse> {
  try {
    const response = await api.post<LoginResponse>('/auth/login', request);
    return response.data;
  } catch (error) {
    throw error as AuthError;
  }
}

/**
 * Refresh access token using refresh token
 * @throws {AuthError} on invalid or expired refresh token
 */
export async function refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
  try {
    const response = await api.post<RefreshTokenResponse>('/auth/refresh', {
      refreshToken,
    });
    return response.data;
  } catch (error) {
    throw error as AuthError;
  }
}

/**
 * Verify email with confirmation code
 * @throws {AuthError} on invalid or expired code
 */
export async function verifyEmail(request: VerifyEmailRequest): Promise<VerifyEmailResponse> {
  try {
    const response = await api.post<VerifyEmailResponse>('/auth/verify', request);
    return response.data;
  } catch (error) {
    throw error as AuthError;
  }
}

/**
 * Resend email verification code
 * @throws {AuthError} on rate limit or user not found
 */
export async function resendCode(request: ResendCodeRequest): Promise<ResendCodeResponse> {
  try {
    const response = await api.post<ResendCodeResponse>('/auth/resend', request);
    return response.data;
  } catch (error) {
    throw error as AuthError;
  }
}

/**
 * Initiate password reset — sends a reset code to the given email address.
 * Backend always returns 200 regardless of whether the email is registered
 * (account enumeration prevention). Errors here are only genuine network or
 * server failures, or a 400 for an empty email body.
 * @throws {AuthError} on network failure or server error
 */
export async function forgotPassword(request: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
  try {
    const response = await api.post<ForgotPasswordResponse>('/auth/forgot-password', request);
    return response.data;
  } catch (error) {
    throw error as AuthError;
  }
}

/**
 * Confirm password reset — validates the reset code and sets the new password.
 * @throws {AuthError} on invalid/expired code, invalid password, rate limit, or server error
 */
export async function confirmPasswordReset(request: ResetPasswordRequest): Promise<ResetPasswordResponse> {
  try {
    const response = await api.post<ResetPasswordResponse>('/auth/reset-password', request);
    return response.data;
  } catch (error) {
    throw error as AuthError;
  }
}

/**
 * Logout (clears tokens locally)
 * No backend call needed as tokens are stateless
 */
export function logout(): void {
  // Tokens will be cleared by useAuth hook
  return;
}
